import { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { FormattedMessage } from "react-intl";
import { sendTagOnFlightOptionsFail, sendTagOnFlightShowMore } from "app/utils/analytics";
import { hasTrainTransportation } from "app/pages/Listing/productsSelector";
import {
	getFilteredFlights,
	getVisibleFlights,
} from "app/pages/Booking/Quotation/quotationSelectors.js";
import {
	fetchFlightOptions,
	showMoreFlights,
} from "app/pages/Booking/Quotation/quotationActionCreators";
import { scrollToElement } from "app/utils/scroller";
import { getMaxLuggageCount } from "app/pages/Booking/bookingSelectors";
import { updateBooking } from "app/pages/Booking/bookingActionCreators";
import QuotationFlights from "app/pages/Booking/Quotation/Flight/QuotationFlights";
import FlightsFilterSortContainer from "app/pages/Booking/Quotation/Flight/filter/FlightsFilterSortContainer";
import QuotationTransfersContainer from "app/pages/Booking/Quotation/Transfer/QuotationTransfersContainer";
import QuotationLuggageContainer from "app/pages/Booking/Quotation/Luggage/QuotationLuggageContainer";
import { FLIGHTS_PAGE_SIZE, HTTP_STATUS_CODES } from "app/constants";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import useOnScreen from "app/pages/.shared/useOnScreen";
import { parseQueries } from "app/utils/routerUtils";
import findIndex from "lodash/findIndex";
import find from "lodash/find";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import Button, { BUTTON_VARIANTS } from "app/pages/.shared/form/Button";
import { fetchStopoverFlightOptions } from "app/pages/Stopover/stopoverActionCreators";

const QuotationFlightsContainer = ({
	brand = {},
	departureDate = 0,
	flights = [],
	visibleFlights = [],
	travelByTrainAvailable = false,
	filteredFlights = [],
	luggageMaxCount = 0,
	updateBooking = () => {},
	showMoreFlights = () => {},
	booking = {},
	quotationCode,
	disableFlightsSelection = false,
	disableBaggagesSelection = false,
	isStopover = false,
	fetchFlightOptions = () => {},
	fetchStopoverFlightOptions = () => {},
}) => {
	const { track } = useAnalytics();
	const selectedFlightCode = booking?.flight?.code;
	const stopoverDuration = booking?.stopoverDuration;
	const query = parseQueries(location.search);
	const { accommodationId } = query;
	const ref = useRef();
	const luggageSectionRef = useRef();
	const selectedFlightRef = useRef(selectedFlightCode);
	const isElementOnScreen = useOnScreen({ ref, triggerOnce: false });

	useEffect(() => {
		if (!isStopover) {
			// This hook is to handle flight selection when flights are filtered

			let flightCodeToSelect = visibleFlights.length > 0 ? visibleFlights[0].code : undefined;

			// we check if the selected flight is among the filtered flight
			if (selectedFlightCode && visibleFlights.length > 0) {
				const isSelectedFlightVisible =
					findIndex(visibleFlights, flight => flight.code === selectedFlightCode) !== -1;
				// if so, we keep the flight selection. If not, we select the first flight of filtered flights
				flightCodeToSelect = isSelectedFlightVisible
					? selectedFlightCode
					: visibleFlights[0].code;
			}

			// if the flight to select is different from the already selected one
			if (flightCodeToSelect !== selectedFlightCode && visibleFlights.length > 0) {
				const flight = find(visibleFlights, flight => {
					return flight.code === flightCodeToSelect;
				});

				// then we update the flight selection
				if (flight) {
					updateBooking({ flight });

					const fetchFlightOptionsFunction = isStopover
						? fetchStopoverFlightOptions
						: fetchFlightOptions;

					const flightOptionsPayload = {
						flight: flight?.code,
						accommodationId,
					};

					if (isStopover) {
						flightOptionsPayload.bookingFlowId = quotationCode;
						flightOptionsPayload.stopOverDuration = stopoverDuration;
					} else {
						flightOptionsPayload.code = quotationCode;
					}

					fetchFlightOptionsFunction(flightOptionsPayload).then(response => {
						const flightOptionsHttpStatus = response.status;

						if (
							!flightOptionsHttpStatus ||
							flightOptionsHttpStatus === HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR ||
							flightOptionsHttpStatus === HTTP_STATUS_CODES.GATEWAY_TIMEOUT ||
							flightOptionsHttpStatus === HTTP_STATUS_CODES.BAD_REQUEST ||
							flightOptionsHttpStatus === HTTP_STATUS_CODES.FORBIDDEN
						) {
							sendTagOnFlightOptionsFail();
						}
					});
				}
			}
		}
	}, [
		visibleFlights,
		selectedFlightCode,
		quotationCode,
		accommodationId,
		isStopover,
		stopoverDuration,
	]);

	useEffect(() => {
		if (isElementOnScreen) {
			track("quote_view_flight");
		}
	}, [isElementOnScreen]);

	// Update the ref whenever selectedFlight changes to always have the latest value
	useEffect(() => {
		selectedFlightRef.current = selectedFlightCode;
	}, [selectedFlightCode]);

	const onFlightSelection = useCallback(
		flight => {
			track("quotation_flight_selected", {
				flightTag: flight?.tag,
			});

			if (flight?.code !== selectedFlightRef.current) {
				updateBooking({ flight });

				const fetchFlightOptionsFunction = isStopover
					? fetchStopoverFlightOptions
					: fetchFlightOptions;

				const flightOptionsPayload = {
					flight: flight?.code,
					accommodationId,
				};

				if (isStopover) {
					flightOptionsPayload.bookingFlowId = quotationCode;
					flightOptionsPayload.stopOverDuration = stopoverDuration;
				} else {
					flightOptionsPayload.code = quotationCode;
				}

				fetchFlightOptionsFunction(flightOptionsPayload).then(response => {
					const flightOptionsHttpStatus = response.status;

					if (
						!flightOptionsHttpStatus ||
						flightOptionsHttpStatus === HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR ||
						flightOptionsHttpStatus === HTTP_STATUS_CODES.GATEWAY_TIMEOUT ||
						flightOptionsHttpStatus === HTTP_STATUS_CODES.BAD_REQUEST ||
						flightOptionsHttpStatus === HTTP_STATUS_CODES.FORBIDDEN
					) {
						sendTagOnFlightOptionsFail();
					}
				});
			}
		},
		[quotationCode, accommodationId, isStopover, stopoverDuration, selectedFlightRef.current]
	);

	const handleShowMoreFlights = useCallback(() => {
		showMoreFlights();
		sendTagOnFlightShowMore(
			visibleFlights.length + FLIGHTS_PAGE_SIZE,
			filteredFlights.length,
			flights.length
		);
	}, [flights.length, visibleFlights.length, filteredFlights.length]);

	const handleLuggageClick = useCallback(() => {
		if (luggageSectionRef.current) {
			scrollToElement({ element: luggageSectionRef.current, disableSmoothScroll: false });
		}
	}, [luggageSectionRef.current]);

	return (
		<>
			{((!disableFlightsSelection && flights.length > 0) || !disableBaggagesSelection) && (
				<div className="quotation__item" ref={ref}>
					{travelByTrainAvailable ? (
						<FormattedMessage
							id={
								disableFlightsSelection
									? "quotation.flight.baggage.only.title"
									: "quotation.transport.title"
							}
						>
							{message => (
								<div className="quotation__headline">
									<Typography
										variant={TYPOGRAPHY_VARIANTS.XL3}
										variantMD={TYPOGRAPHY_VARIANTS.XL4}
										isBold
										className="quotation__title"
										component="h2"
									>
										{message}
									</Typography>
								</div>
							)}
						</FormattedMessage>
					) : (
						<FormattedMessage
							id={
								disableFlightsSelection
									? "quotation.flight.baggage.only.title"
									: "quotation.flight.title"
							}
						>
							{message => (
								<div className="quotation__headline">
									<Typography
										variant={TYPOGRAPHY_VARIANTS.XL}
										variantMD={TYPOGRAPHY_VARIANTS.XL4}
										isBold
										className="quotation__title"
										component="h2"
									>
										{message}
									</Typography>
								</div>
							)}
						</FormattedMessage>
					)}
					{!disableFlightsSelection && (
						<>
							<div className="quotation__filter-sort">
								<FlightsFilterSortContainer />
							</div>
							<div className="quotation__content">
								<QuotationFlights
									travelByTrainAvailable={travelByTrainAvailable}
									flights={visibleFlights}
									luggageMaxCount={luggageMaxCount}
									onLuggageClick={handleLuggageClick}
									onChange={onFlightSelection}
									departureDate={departureDate}
									brand={brand.code}
								/>

								{visibleFlights.length < visibleFlights.flightsPageSize ||
								visibleFlights.length === filteredFlights.length ||
								visibleFlights.length === flights.length ? (
									false
								) : (
									<div className="quotation__show-more">
										<Button
											variant={BUTTON_VARIANTS.STROKE}
											label={
												<FormattedMessage id="general.more.options.button.label" />
											}
											onClick={handleShowMoreFlights}
											data-testid="show-more-flight-button"
										/>
									</div>
								)}
							</div>
						</>
					)}

					{!disableBaggagesSelection && (
						<div className="quotation__luggages" ref={luggageSectionRef}>
							<QuotationLuggageContainer
								disableFlightSelection={disableFlightsSelection}
							/>
						</div>
					)}
				</div>
			)}
			<QuotationTransfersContainer />
		</>
	);
};

QuotationFlightsContainer.propTypes = {
	departureDate: PropTypes.number,
	brand: PropTypes.object,
	flights: PropTypes.array,
	visibleFlights: PropTypes.array,
	isStopover: PropTypes.bool,
	disableFlightsSelection: PropTypes.bool,
	disableBaggagesSelection: PropTypes.bool,
	travelByTrainAvailable: PropTypes.bool,
	filteredFlights: PropTypes.array,
	luggageMaxCount: PropTypes.number,
	updateBooking: PropTypes.func,
	showMoreFlights: PropTypes.func,
	booking: PropTypes.object,
	quotationCode: PropTypes.string,
	fetchFlightOptions: PropTypes.func,
	fetchStopoverFlightOptions: PropTypes.func,
};

const mapStateToProps = state => ({
	departureDate: state.booking.departureDate,
	brand: state.brand,
	flights: state.quotation.flights,
	visibleFlights: getVisibleFlights(state),
	travelByTrainAvailable: hasTrainTransportation(state),
	filteredFlights: getFilteredFlights(state),
	luggageMaxCount: getMaxLuggageCount(state),
	quotationCode: state.quotation.code,
	booking: state.booking,
});

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			updateBooking,
			showMoreFlights,
			fetchFlightOptions,
			fetchStopoverFlightOptions,
		},
		dispatch
	);
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(QuotationFlightsContainer);
